import { useState } from 'react';
import DataTable from './DataTable';
import Pagination from './Pagination';
import SearchBar from './SearchBar';
import ConfirmDialog from './ConfirmDialog';
import ResourceForm from './ResourceForm';
import { useToast } from '../context/ToastContext';
import { navigate } from '../lib/router';

const LIMIT = 20;

// Full CRUD list page driven by a resource config:
// { title, singular, columns, fields, hooks:{useList,useCreate,useUpdate,useRemove},
//   canCreate=true, canEdit=true, detailPath?(row), rowActions?(row,{edit,del}) }
export default function ResourceListPage({ config }) {
  const { title, singular, columns, fields, hooks, canCreate = true, canEdit = true, detailPath, extraActions } = config;
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const { data, isLoading, error } = hooks.useList({ page, limit: LIMIT, search });
  const rows = data?.data || [];

  const delM = hooks.useRemove({
    onSuccess: () => { toast.success('Deleted'); setDeleting(null); },
    onError: (e) => toast.error(e.message || 'Delete failed'),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row) => { setEditing(row); setFormOpen(true); };
  const onSearch = (v) => { setSearch(v); setPage(1); };

  return (
    <div className="page">
      <div className="page__header">
        <h1>{title}</h1>
        {canCreate && <button className="btn btn--primary" onClick={openCreate}>+ New</button>}
      </div>

      <div className="page__toolbar">
        <SearchBar value={search} onSearch={onSearch} placeholder={`Search ${title.toLowerCase()}…`} />
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        error={error}
        emptyText={`No ${title.toLowerCase()} yet.`}
        actions={(row) => (
          <>
            {detailPath && <button className="btn btn--sm" onClick={() => navigate(detailPath(row))}>View</button>}
            {extraActions?.(row)}
            {canEdit && <button className="btn btn--sm" onClick={() => openEdit(row)}>Edit</button>}
            <button className="btn btn--sm btn--danger" onClick={() => setDeleting(row)}>Delete</button>
          </>
        )}
      />

      {data && <Pagination page={page} limit={LIMIT} total={data.total} onPage={setPage} />}

      {(canCreate || canEdit) && (
        <ResourceForm
          title={editing ? `Edit ${singular}` : `New ${singular}`}
          open={formOpen}
          record={editing}
          fields={fields}
          useCreate={hooks.useCreate}
          useUpdate={hooks.useUpdate}
          onClose={() => setFormOpen(false)}
          onSaved={() => setFormOpen(false)}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        message={deleting ? `Delete this ${singular.toLowerCase()}? This cannot be undone.` : ''}
        pending={delM.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => delM.mutate(deleting.id)}
      />
    </div>
  );
}
